import React, { useState, useRef, useCallback, useEffect } from 'react';
import { lockTranslation, unlockTranslation } from '../api';
import SaveAndLockConfirmPopover from './SaveAndLockConfirmPopover';
import {
  getLockVisualState,
  getLockTooltipContent,
  getLockAriaLabel,
  normalizeLockState,
} from '../utils/translationLock';
import { requestOpenConfirm, releaseConfirm } from '../utils/confirmPopoverCoordinator';

function UnlockIcon() {
  return (
    <svg
      className="lock-icon lock-icon--unlocked"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </svg>
  );
}

function LockIcon({ className }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function LockStatusTooltip({ open, position, content, onMouseEnter, onMouseLeave }) {
  if (!open || !content) return null;
  return (
    <span
      className={`lock-tooltip lock-tooltip--${content.accent}`}
      role="tooltip"
      style={{ top: position.top, left: position.left }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <span className="lock-tooltip-title">{content.title}</span>
      <span className="lock-tooltip-description">{content.description}</span>
      {content.meta && <span className="lock-tooltip-meta">{content.meta}</span>}
    </span>
  );
}

function TranslationLockControl({
  resourceType,
  translationId,
  parentId,
  isLocked,
  lockedByUserId,
  lockedAt,
  currentUserId,
  onLockStatusChange,
  onNotify,
}) {
  const [loading, setLoading] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const closeTimerRef = useRef(null);

  const lock = normalizeLockState({
    isLocked,
    lockedByUserId,
    lockedAt,
  });
  const visualState = getLockVisualState(lock, currentUserId);
  const tooltipContent = getLockTooltipContent(visualState, lock);
  const ariaLabel = getLockAriaLabel(visualState, lock);
  const canMutate = visualState !== 'other';

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const updateTooltipPosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom + 8,
      left: Math.min(rect.left, window.innerWidth - 280),
    });
  }, []);

  const openTooltip = useCallback(() => {
    clearCloseTimer();
    updateTooltipPosition();
    setTooltipOpen(true);
  }, [clearCloseTimer, updateTooltipPosition]);

  const scheduleCloseTooltip = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setTooltipOpen(false), 200);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => () => releaseConfirm(translationId), [translationId]);

  const closeConfirm = useCallback(() => {
    setConfirmOpen(false);
    releaseConfirm(translationId);
  }, [translationId]);

  const openConfirm = useCallback(() => {
    requestOpenConfirm(translationId, () => setConfirmOpen(false));
    setConfirmOpen(true);
    setTooltipOpen(false);
  }, [translationId]);

  useEffect(() => {
    if (!tooltipOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setTooltipOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [tooltipOpen]);

  const handleToggleLock = async () => {
    if (loading || !canMutate) return;

    if (visualState === 'unlocked') {
      openConfirm();
      return;
    }

    await performUnlock();
  };

  const performLock = async () => {
    closeConfirm();
    setLoading(true);
    try {
      const payload = {
        reqId: translationId,
        resourceType,
        parentId,
      };
      const result = await lockTranslation(payload);

      if (result.lock && onLockStatusChange) {
        onLockStatusChange(result.lock);
      }
      if (result.warning && onNotify) {
        onNotify({ type: 'error', text: result.warning });
      } else if (onNotify) {
        onNotify({
          type: 'success',
          text: 'Translation saved to Translation Memory and locked.',
        });
      }
    } catch (err) {
      if (err.lock && onLockStatusChange) {
        onLockStatusChange(err.lock);
      }
      if (onNotify) {
        onNotify({
          type: 'error',
          text: err.message || 'Lock operation failed.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const performUnlock = async () => {
    setLoading(true);
    try {
      const payload = {
        reqId: translationId,
        resourceType,
        parentId,
      };
      const result = await unlockTranslation(payload);

      if (result.lock && onLockStatusChange) {
        onLockStatusChange(result.lock);
      }
    } catch (err) {
      if (err.lock && onLockStatusChange) {
        onLockStatusChange(err.lock);
      }
      if (onNotify) {
        onNotify({
          type: 'error',
          text: err.message || 'Unlock operation failed.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && canMutate && !loading) {
      e.preventDefault();
      handleToggleLock();
    }
  };

  const iconClass = visualState === 'mine'
    ? 'lock-icon lock-icon--mine'
    : visualState === 'other'
      ? 'lock-icon lock-icon--other'
      : 'lock-icon lock-icon--unlocked';

  return (
    <span className="lock-control-wrapper">
      <button
        ref={btnRef}
        type="button"
        className={`lock-control-btn lock-control-btn--${visualState}${!canMutate ? ' lock-control-btn--readonly' : ''}`}
        aria-label={ariaLabel}
        aria-pressed={lock.isLocked}
        disabled={loading}
        onClick={canMutate ? handleToggleLock : undefined}
        onKeyDown={handleKeyDown}
        onMouseEnter={openTooltip}
        onMouseLeave={scheduleCloseTooltip}
        onFocus={openTooltip}
        onBlur={scheduleCloseTooltip}
      >
        {loading ? (
          <span className="spinner lock-control-spinner" aria-hidden="true" />
        ) : visualState === 'unlocked' ? (
          <UnlockIcon />
        ) : (
          <LockIcon className={iconClass} />
        )}
      </button>
      <LockStatusTooltip
        open={tooltipOpen}
        position={tooltipPos}
        content={tooltipContent}
        onMouseEnter={clearCloseTimer}
        onMouseLeave={scheduleCloseTooltip}
      />
      <SaveAndLockConfirmPopover
        open={confirmOpen}
        anchorRef={btnRef}
        onClose={closeConfirm}
        onConfirm={performLock}
        confirming={loading}
      />
    </span>
  );
}

export default TranslationLockControl;
